import * as THREE from 'three';

/**
 * Performance utility functions for VRM viewer
 */

export interface DevicePerformanceTier {
  tier: 'low' | 'medium' | 'high';
  pixelRatio: number;
  shadowMapSize: number;
  antialias: boolean;
  maxBones: number;
}

/**
 * Detect device performance tier based on various factors
 * @returns Performance tier configuration
 */
export function detectDevicePerformanceTier(): DevicePerformanceTier {
  const gl = document.createElement('canvas').getContext('webgl2') || 
             document.createElement('canvas').getContext('webgl');
  
  if (!gl) {
    return {
      tier: 'low',
      pixelRatio: 1,
      shadowMapSize: 512,
      antialias: false,
      maxBones: 30
    };
  }
  
  // Check device capabilities
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
  const renderer = gl.getParameter(gl.RENDERER);
  
  // Check for powerful GPUs
  const isPowerfulGPU = 
    renderer.includes('NVIDIA') || 
    renderer.includes('AMD') || 
    renderer.includes('Intel Iris') ||
    (renderer.includes('Apple') && !isMobile);
  
  // Determine tier
  if (isMobile || maxTextureSize < 4096) {
    return {
      tier: 'low',
      pixelRatio: Math.min(window.devicePixelRatio, 1.5),
      shadowMapSize: 512,
      antialias: false,
      maxBones: 30
    };
  } else if (isPowerfulGPU && maxTextureSize >= 8192) {
    return {
      tier: 'high',
      pixelRatio: Math.min(window.devicePixelRatio, 2),
      shadowMapSize: 2048,
      antialias: true,
      maxBones: 100
    };
  } else {
    return {
      tier: 'medium',
      pixelRatio: Math.min(window.devicePixelRatio, 1.5),
      shadowMapSize: 1024,
      antialias: true,
      maxBones: 60
    };
  }
}

/**
 * Get optimized renderer settings based on performance tier
 */
export function getOptimizedRendererSettings(tier: DevicePerformanceTier) {
  return {
    antialias: tier.antialias,
    alpha: true,
    powerPreference: tier.tier === 'high' ? 'high-performance' : 'default',
    failIfMajorPerformanceCaveat: true,
    preserveDrawingBuffer: false,
    depth: true,
    stencil: false,
    premultipliedAlpha: true
  };
}

/**
 * Monitor performance and adjust quality if needed
 */
export class PerformanceMonitor {
  private frameCount = 0;
  private lastTime = performance.now();
  private fps = 60;
  private readonly targetFPS = 30;
  private readonly sampleInterval = 60; // Sample every 60 frames
  
  update(): number {
    this.frameCount++;
    
    if (this.frameCount % this.sampleInterval === 0) {
      const currentTime = performance.now();
      const delta = currentTime - this.lastTime;
      this.fps = (this.sampleInterval * 1000) / delta;
      this.lastTime = currentTime;
    }
    
    return this.fps;
  }
  
  shouldReduceQuality(): boolean {
    return this.fps < this.targetFPS;
  }
  
  reset(): void {
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.fps = 60;
  }
}

export function createOptimizedRenderer(container: HTMLElement) {
  const tier = detectDevicePerformanceTier();
  const isMobile = tier.tier === 'low'; // Assuming 'low' tier means mobile
  
  const renderer = new THREE.WebGLRenderer({
    antialias: tier.antialias,
    alpha: true,
    powerPreference: tier.tier === 'high' ? 'high-performance' : 'default',
    failIfMajorPerformanceCaveat: false,
    preserveDrawingBuffer: false,
    premultipliedAlpha: false, // 透明度計算の改善
    logarithmicDepthBuffer: false,
    precision: 'highp'
  });
  
  // Set pixel ratio based on device
  const pixelRatio = tier.antialias ? Math.min(window.devicePixelRatio, 2) : 1;
  renderer.setPixelRatio(pixelRatio);
  
  // Set size
  const { width, height } = container.getBoundingClientRect();
  renderer.setSize(width, height);
  
  // Set render settings
  renderer.shadowMap.enabled = tier.antialias;
  renderer.shadowMap.type = tier.antialias ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  
  // Clear color設定（透明）
  renderer.setClearColor(0x000000, 0);
  
  // モバイルでの追加設定
  if (isMobile && renderer.domElement) {
    renderer.domElement.style.backgroundColor = 'transparent';
    renderer.domElement.style.opacity = '1';
    // iOS Safari対策
    renderer.domElement.style.webkitBackfaceVisibility = 'hidden';
  }
  
  return renderer;
}